import { useState, type Dispatch, type SetStateAction } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { DATE_RANGE_CHOICE_LABELS } from "@features/sales-v3/top-level-filters/components/date-filters";
import { dateRangeOptionsForTimeGranularity } from "@north-beam/nb-common";
import { AccountingModeSwitch } from "@shared/accounting-mode-switch";
import { AttributionModelAndWindowSelect } from "@shared/attribution-model-and-window-select";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { FlatSelect } from "@shared/flat-select";
import { Modal } from "@shared/modal";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";

import { useProductAnalyticsContext } from "../hooks";

interface FilterModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const ChartControlsModal = ({
  isModalOpen,
  setIsModalOpen,
}: FilterModalProps) => {
  const {
    filters,
    setDateRange,
    setAccountingMode,
    setAttributionModel,
    setAttributionWindow,
  } = useProductAnalyticsContext();

  const { user } = useUser();

  const amEnum = user.attributionMethodEnum;
  const [tempAccountMode, setTempAccountMode] = useState(
    filters.accountingMode,
  );
  const [tempAttributionModel, setTempAttributionModel] = useState(
    filters.attributionModel,
  );
  const [tempAttributionWindow, setTempAttributionWindow] = useState(
    filters.attributionWindow,
  );
  const [tempDateRange, setTempDateRange] = useState<any>(filters.dateRange);

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      titleContent={<h2>TIME PERIOD FILTERS</h2>}
    >
      <div className="m-w-[900px]">
        <div className="flex flex-col gap-2 items-start mt-4">
          <AccountingModeSwitch
            accrualOnly
            value={tempAccountMode}
            onChange={(value) => {
              setTempAccountMode(value);
            }}
          />
          <AttributionModelAndWindowSelect
            options={amEnum.reactSelectOptions.filter(
              (option) => option.value === "northbeam_custom",
            )}
            timeGranularity="daily"
            isAttributionWindowVisible
            attributionModel={tempAttributionModel}
            attributionWindow={tempAttributionWindow}
            attributionWindowOptionsFilter={(option) => {
              return option.value === "1" || option.value === "30";
            }}
            onAttributionModelChange={(value) => {
              setTempAttributionModel(value);
            }}
            onAttributionWindowChange={(value) => {
              setTempAttributionWindow(value);
            }}
          />
          <div>
            <label className="label text-xs leading-5 flex gap-1 mb-[3px]">
              TIME PERIOD
            </label>
            <FlatSelect
              options={[
                {
                  label: "",
                  value: "custom",
                  renderOption: ({ isSelected }) => (
                    <DateRangeCalendarSelect
                      calendarClassName="-ml-1 mt-0.5"
                      value={tempDateRange}
                      onApply={(v) => {
                        setTempDateRange(v);
                      }}
                      buttonComponent={(ref, refProps) => (
                        <div
                          ref={ref}
                          {...refProps}
                          className={clsx(
                            " h-full flex items-center cursor-pointer",
                            {
                              "text-white": isSelected,
                            },
                          )}
                        >
                          Custom
                        </div>
                      )}
                    />
                  ),
                },
                ...dateRangeOptionsForTimeGranularity("daily")
                  .filter(
                    (i) => !["lastMonth", "monthToDate"].includes(i.value),
                  )
                  .map((e) => {
                    return {
                      label:
                        DATE_RANGE_CHOICE_LABELS[
                          e.value as keyof typeof DATE_RANGE_CHOICE_LABELS
                        ],
                      value: e.value,
                      onClick: () => {
                        setTempDateRange(e.value);
                      },
                    };
                  })
                  .filter((item) => item.label),
              ]}
              selected={{
                label:
                  DATE_RANGE_CHOICE_LABELS[
                    tempDateRange as keyof typeof DATE_RANGE_CHOICE_LABELS
                  ],
                value: DATE_RANGE_CHOICE_LABELS[
                  tempDateRange as keyof typeof DATE_RANGE_CHOICE_LABELS
                ]
                  ? tempDateRange
                  : "custom",
              }}
              setSelected={(option) => {
                if (typeof option.value === "string") {
                  return;
                } else {
                  setTempDateRange(option.value);
                }
              }}
            />
          </div>
        </div>
        <div className="flex justify-between pt-6">
          <TertiaryButton
            onClick={() => {
              setTempAccountMode(filters.accountingMode);
              setTempAttributionModel(filters.attributionModel);
              setTempAttributionWindow(filters.attributionWindow);
              setTempDateRange(filters.dateRange);
              setIsModalOpen(false);
            }}
            className="uppercase"
          >
            cancel
          </TertiaryButton>
          <PrimaryButton
            onClick={() => {
              setDateRange(tempDateRange);
              setAccountingMode(tempAccountMode);
              setAttributionModel(tempAttributionModel);
              setAttributionWindow(tempAttributionWindow);
              setIsModalOpen(false);
            }}
            className="uppercase"
          >
            Apply Filters
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default ChartControlsModal;
